import { TTopicState, TopicAction } from "./topic.state";
import { SearchActions } from "~/types/actions.enum";
import { NormalizedTopic, UniqueTopicIdType } from "~/types/search.types";

/**
 * Combines a Topic's disclosureId, categoryId, and its exploreId (topicId) into a unique string
 */
const getUniqueTopicId = (
    topicItem: NormalizedTopic | null | undefined
): UniqueTopicIdType | null => {
    if (!topicItem) {
        return null;
    }

    const { disclosureId, categoryId, exploreId } = topicItem;
    return `${disclosureId}|${categoryId}|${exploreId}`;
};

// ✅ Topic reducer logic
export const topicReducer = (state: TTopicState, action: TopicAction): TTopicState => {
    const getTopicById = (topicId: number | string) =>
        state.topics.find(item => item.exploreId === Number(topicId));

    switch (action.type) {
        case SearchActions.SET_TOPIC_ITEMS: {
            return {
                ...state,
                topics: action.payload,
                topic: null,
                selectedTopicIds: [],
                totalSelectedDocs: 0
            };
        }

        case SearchActions.SET_ACTIVE_TOPIC:
            return { ...state, topic: action.payload };

        case SearchActions.SET_SELECTED_TOPIC: {
            const tempTopic = getTopicById(action.payload);

            if (!tempTopic) {
                return { ...state };
            }

            const uniqueId = getUniqueTopicId(tempTopic);

            if (!uniqueId || state.selectedTopicIds.includes(uniqueId)) {
                return { ...state };
            }

            // Increment the total number of selected documents count
            let docsCount = state.totalSelectedDocs;
            docsCount += tempTopic.count > -1 ? tempTopic.count : 0;

            return {
                ...state,
                totalSelectedDocs: docsCount,
                selectedTopicIds: [...state.selectedTopicIds, uniqueId]
            };
        }

        case SearchActions.SET_SELECTED_TOPICS: {
            const tempTopics = action.payload.map(item => getTopicById(item));

            const uniqueStringIds = tempTopics
                .map(item => getUniqueTopicId(item))
                .filter(item => typeof item === "string") as UniqueTopicIdType[];

            if (!uniqueStringIds || !Array.isArray(uniqueStringIds)) {
                return { ...state };
            }

            const updatedCount = tempTopics.reduce((sum, item) => (sum += item?.count || 0), 0);

            return {
                ...state,
                totalSelectedDocs: updatedCount,
                selectedTopicIds: uniqueStringIds
            };
        }

        case SearchActions.REMOVE_SELECTED_TOPIC: {
            const topicItem = getTopicById(action.payload);
            let documentsCount = state.totalSelectedDocs;
            documentsCount -= topicItem ? topicItem.count : 0;

            const uniqueStringId = getUniqueTopicId(topicItem);

            return {
                ...state,
                totalSelectedDocs: documentsCount,
                selectedTopicIds: state.selectedTopicIds.filter(item => item !== uniqueStringId)
            };
        }

        case SearchActions.SET_TOPIC_COUNTS: {
            if (!action.payload) {
                return { ...state };
            }

            const updatedTopics = state.topics.map(topic => {
                const newCount = action.payload![topic.exploreId];

                if (newCount !== undefined) {
                    return { ...topic, count: newCount }; // clone and update
                }
                return topic;
            });

            return { ...state, topics: updatedTopics };
        }

        case SearchActions.SET_TOPICS_DOCS_COUNT:
            return {
                ...state,
                totalSelectedDocs: state.totalSelectedDocs + action.payload
            };

        default:
            return state;
    }
};
